import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, Marker, DirectionsRenderer, TrafficLayer, useJsApiLoader } from '@react-google-maps/api';
import { collection, getDocs, query, where, doc, getDoc, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import LocationSearch from '../components/LocationSearch';
import { FaMapMarkerAlt, FaFlagCheckered, FaSave, FaFolderOpen } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const libraries = ['places', 'marker'];

const RoutesEngine = () => {
  const mapRef = useRef(null);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [tripType, setTripType] = useState('AM');
  const [startLocation, setStartLocation] = useState(null);
  const [endLocation, setEndLocation] = useState(null);
  const [selectedBus, setSelectedBus] = useState(null);
  const [buses, setBuses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [directions, setDirections] = useState(null);
  const [error, setError] = useState('');
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [routeDuration, setRouteDuration] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [totalDuration, setTotalDuration] = useState(0);
  const [totalWaitTime, setTotalWaitTime] = useState(0);
  const [totalDurationWithWait, setTotalDurationWithWait] = useState(0);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [optimizedWaypointOrder, setOptimizedWaypointOrder] = useState([]);
  const [isSortedByRoute, setIsSortedByRoute] = useState(false);
  const [isManualOrder, setIsManualOrder] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showLoadRouteModal, setShowLoadRouteModal] = useState(false);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [trafficModel, setTrafficModel] = useState('BEST_GUESS');
  const [useTrafficOptimization, setUseTrafficOptimization] = useState(true);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showWeeklyTrafficView, setShowWeeklyTrafficView] = useState(false);
  const [directionsSegments, setDirectionsSegments] = useState([]);
  const [globalStudentSequence, setGlobalStudentSequence] = useState([]);
  const [additionalStops, setAdditionalStops] = useState([]);

  // Google DirectionsService has a strict limit on intermediate waypoints per request.
  // We segment routes when exceeding this number.
  const MAX_INTERMEDIATE_WAYPOINTS = 23; // conservative across APIs
  const DIRECTIONS_TIMEOUT_MS = 20000; // 20s safety timeout per request

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const center = {
    lat: -1.2921,
    lng: 36.8219
  };

  const onMapLoad = useCallback((loadedMap) => {
    mapRef.current = loadedMap;
  }, []);

  const markerStyles = `
    .custom-marker {
      position: relative;
      cursor: pointer;
    }
    .custom-marker:hover {
      transform: scale(1.1);
      transition: transform 0.2s;
    }
  `;

  // Auto-reoptimize route when students, locations, stops, or traffic settings change
  // Note: We'll define this effect after optimizeRoute to avoid initialization issues

  const updateRouteWithManualOrder = useCallback((waypoints) => {
    if (!startLocation || !endLocation || !window.google) {
      setError('Please set start and end locations before optimizing');
      return;
    }

    setIsOptimizing(true);
    setError('');
    setRouteDuration(null);
    setRouteDistance(null);
    setDirections(null);
    setDirectionsSegments([]);
    setGlobalStudentSequence([]);

    (async () => {
      try {
        // Calculate departure time based on selected time
        const calculatedDepartureTime = calculateDepartureTime(selectedTime);

        // Add additional stops to waypoints
        const additionalStopWaypoints = additionalStops.map(stop => ({
          location: new window.google.maps.LatLng(stop.lat, stop.lng),
          stopover: true,
          stopId: stop.id
        }));

        const allWaypoints = [...waypoints, ...additionalStopWaypoints];

        if (allWaypoints.length > MAX_INTERMEDIATE_WAYPOINTS) {
          // Segment the manual order into multiple requests
          const { segmentResults, totalDurationSeconds, totalDistanceMeters, globalStudentSequence: order } = await computeSegmentedDirections(
            startLocation,
            endLocation,
            allWaypoints,
            false,
            calculatedDepartureTime
          );

          setDirections(null);
          setDirectionsSegments(segmentResults);
          setGlobalStudentSequence(order && order.length ? order : allWaypoints.map(w => w.studentId || w.stopId).filter(Boolean));

          const waitTimePerStop = 300;
          const numberOfStops = allWaypoints.length;
          const totalWaitTimeSeconds = waitTimePerStop * numberOfStops;
          const totalDurationWithWait = totalDurationSeconds + totalWaitTimeSeconds;

          const durationText = Math.floor(totalDurationWithWait / 3600) + ' hours ' +
                               Math.floor((totalDurationWithWait % 3600) / 60) + ' mins';
          const distanceText = (totalDistanceMeters / 1000).toFixed(1) + ' km';

          setRouteDuration(durationText);
          setRouteDistance(distanceText);
          setTotalDuration(totalDurationSeconds);
          setTotalWaitTime(totalWaitTimeSeconds);
          setTotalDurationWithWait(totalDurationWithWait);
        } else {
          // Single-request manual routing (existing behavior)
          const directionsService = new window.google.maps.DirectionsService();
          const request = {
            origin: new window.google.maps.LatLng(startLocation.lat, startLocation.lng),
            destination: new window.google.maps.LatLng(endLocation.lat, endLocation.lng),
            waypoints: (allWaypoints || []).map(({ location, stopover }) => ({ location, stopover })),
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode.DRIVING,
            drivingOptions: useTrafficOptimization ? {
              departureTime: calculatedDepartureTime,
              trafficModel: window.google.maps.TrafficModel[trafficModel]
            } : undefined,
            avoidTolls: true
          };

          const result = await routeWithTimeout(directionsService, request);
          setDirections(result);
          setDirectionsSegments([]);
          setGlobalStudentSequence(allWaypoints.map(w => w.studentId || w.stopId).filter(Boolean));

          const totalDuration = result.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0);
          const totalDistance = result.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0);

          const waitTimePerStop = 300;
          const numberOfStops = allWaypoints.length;
          const totalWaitTime = waitTimePerStop * numberOfStops;
          const totalDurationWithWait = totalDuration + totalWaitTime;

          const durationText = Math.floor(totalDurationWithWait / 3600) + ' hours ' +
                               Math.floor((totalDurationWithWait % 3600) / 60) + ' mins';
          const distanceText = (totalDistance / 1000).toFixed(1) + ' km';

          setRouteDuration(durationText);
          setRouteDistance(distanceText);
          setTotalDuration(totalDuration);
          setTotalWaitTime(totalWaitTime);
          setTotalDurationWithWait(totalDurationWithWait);
        }
      } catch (err) {
        setError(`Failed to update route: ${err.message}`);
        setDirections(null);
        setDirectionsSegments([]);
        setGlobalStudentSequence([]);
      } finally {
        setIsOptimizing(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocation, endLocation, selectedTime, useTrafficOptimization, trafficModel, additionalStops]);

  const optimizeRoute = useCallback(() => {
    if (!startLocation || !endLocation || !window.google) {
      setError('Please set start and end locations before optimizing');
      return;
    }

    setIsOptimizing(true);
    setError('');
    setRouteDuration(null);
    setRouteDistance(null);
    setIsManualOrder(false);
    setDirections(null);
    setDirectionsSegments([]);
    setGlobalStudentSequence([]);

    // Prepare inputs
    const selectedStudentsWithLocations = students
      .filter(student => selectedStudents.includes(student.id) && student.location)
      .map(student => ({
        location: new window.google.maps.LatLng(student.location.lat, student.location.lng),
        stopover: true,
        studentId: student.id
      }));

    // Add additional stops as waypoints
    const additionalStopWaypoints = additionalStops.map(stop => ({
      location: new window.google.maps.LatLng(stop.lat, stop.lng),
      stopover: true,
      stopId: stop.id
    }));

    // Combine student waypoints and additional stops
    const allWaypoints = [...selectedStudentsWithLocations, ...additionalStopWaypoints];

    const calculatedDepartureTime = calculateDepartureTime(selectedTime);
    const directionsService = new window.google.maps.DirectionsService();

    (async () => {
      try {
        // Handle direct route (no waypoints) or route with waypoints
        if (allWaypoints.length === 0) {
          // Direct route from start to end
          const request = {
            origin: new window.google.maps.LatLng(startLocation.lat, startLocation.lng),
            destination: new window.google.maps.LatLng(endLocation.lat, endLocation.lng),
            travelMode: window.google.maps.TravelMode.DRIVING,
            drivingOptions: useTrafficOptimization ? {
              departureTime: calculatedDepartureTime,
              trafficModel: window.google.maps.TrafficModel[trafficModel]
            } : undefined,
            avoidTolls: true
          };

          const result = await routeWithTimeout(directionsService, request);
          setDirections(result);
          setDirectionsSegments([]);
          setGlobalStudentSequence([]);

          const totalDuration = result.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0);
          const totalDistance = result.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0);

          const durationText = Math.floor(totalDuration / 3600) + ' hours ' + 
                               Math.floor((totalDuration % 3600) / 60) + ' mins';
          const distanceText = (totalDistance / 1000).toFixed(1) + ' km';

          setRouteDuration(durationText);
          setRouteDistance(distanceText);
          setTotalDuration(totalDuration);
          setTotalWaitTime(0);
          setTotalDurationWithWait(totalDuration);
          setOptimizedWaypointOrder([]);
        } else if (allWaypoints.length > MAX_INTERMEDIATE_WAYPOINTS) {
          // Segment and compute multiple Directions requests
          const { segmentResults, totalDurationSeconds, totalDistanceMeters, globalStudentSequence: order } = await computeSegmentedDirections(
            startLocation,
            endLocation,
            allWaypoints,
            true,
            calculatedDepartureTime
          );

          setDirections(null);
          setDirectionsSegments(segmentResults);
          setGlobalStudentSequence(order);

          const waitTimePerStop = 300;
          const numberOfStops = allWaypoints.length;
          const totalWaitTimeSeconds = waitTimePerStop * numberOfStops;
          const totalDurationWithWait = totalDurationSeconds + totalWaitTimeSeconds;

          const durationText = Math.floor(totalDurationWithWait / 3600) + ' hours ' +
                               Math.floor((totalDurationWithWait % 3600) / 60) + ' mins';
          const distanceText = (totalDistanceMeters / 1000).toFixed(1) + ' km';

          setRouteDuration(durationText);
          setRouteDistance(distanceText);
          setTotalDuration(totalDurationSeconds);
          setTotalWaitTime(totalWaitTimeSeconds);
          setTotalDurationWithWait(totalDurationWithWait);

          // With segmented optimization, we don't compute a single global waypoint order
          setOptimizedWaypointOrder([]);
        } else {
          // Single-request optimized routing (existing behavior)
          const request = {
            origin: new window.google.maps.LatLng(startLocation.lat, startLocation.lng),
            destination: new window.google.maps.LatLng(endLocation.lat, endLocation.lng),
            waypoints: (allWaypoints || []).map(({ location, stopover }) => ({ location, stopover })),
            optimizeWaypoints: true,
            travelMode: window.google.maps.TravelMode.DRIVING,
            drivingOptions: useTrafficOptimization ? {
              departureTime: calculatedDepartureTime,
              trafficModel: window.google.maps.TrafficModel[trafficModel]
            } : undefined,
            avoidTolls: true
          };

          const result = await routeWithTimeout(directionsService, request);
          setDirections(result);
          setDirectionsSegments([]);
          setGlobalStudentSequence([]);

          const totalDuration = result.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0);
          const totalDistance = result.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0);

          const waitTimePerStop = 300; // 5 minutes in seconds
          const numberOfStops = allWaypoints.length;
          const totalWaitTime = waitTimePerStop * numberOfStops;
          const totalDurationWithWait = totalDuration + totalWaitTime;

          const durationText = Math.floor(totalDurationWithWait / 3600) + ' hours ' + 
                               Math.floor((totalDurationWithWait % 3600) / 60) + ' mins';
          const distanceText = (totalDistance / 1000).toFixed(1) + ' km';

          setRouteDuration(durationText);
          setRouteDistance(distanceText);
          setTotalDuration(totalDuration);
          setTotalWaitTime(totalWaitTime);
          setTotalDurationWithWait(totalDurationWithWait);
          setOptimizedWaypointOrder(result.routes[0].waypoint_order || []);
        }
      } catch (err) {
        setError(`Failed to optimize route: ${err.message}`);
        setDirections(null);
        setDirectionsSegments([]);
        setGlobalStudentSequence([]);
        setOptimizedWaypointOrder([]);
      } finally {
        setIsOptimizing(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocation, endLocation, selectedStudents, students, selectedTime, useTrafficOptimization, trafficModel, additionalStops]);

  // Auto-reoptimize route when students, locations, stops, or traffic settings change
  useEffect(() => {
    // Avoid re-entrancy while a previous optimization is in-flight
    if (isOptimizing) return;
    if (startLocation && endLocation) {
      // Show route even if there are no waypoints (direct route from start to end)
      optimizeRoute();
    } else {
      setDirections(null);
      setDirectionsSegments([]);
      setGlobalStudentSequence([]);
      setRouteDuration(null);
      setRouteDistance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudents, startLocation, endLocation, students, selectedTime, useTrafficOptimization, trafficModel, additionalStops]);

  const fetchRosters = async () => {
    try {
      const rostersRef = collection(db, 'rosters');
      const q = query(rostersRef, where('status', '==', 'active'));
      const querySnapshot = await getDocs(q);
      const rostersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        students: doc.data().students || [] // Ensure students array exists
      }));
      setRosters(rostersData);
    } catch (error) {
      console.error('Error fetching rosters:', error);
      setRosters([]);
    }
  };

  const fetchBuses = async () => {
    try {
      const vehiclesRef = collection(db, 'vehicles');
      const q = query(
        vehiclesRef,
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      const busesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBuses(busesData);
    } catch (error) {
      console.error('Error fetching buses:', error);
      setBuses([]);
    }
  };

  useEffect(() => {
    fetchRosters();
    fetchBuses();
    checkForRouteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const savedStart = localStorage.getItem('startLocation');
    const savedEnd = localStorage.getItem('endLocation');
    if (savedStart) setStartLocation(JSON.parse(savedStart));
    if (savedEnd) setEndLocation(JSON.parse(savedEnd));
  }, []);

  const handleRosterSelect = async (rosterId) => {
    if (!rosterId) {
      setSelectedRoster(null);
      setStudents([]);
      setSelectedStudents([]);
      return;
    }

    const selectedRosterData = rosters.find(r => r.id === rosterId);
    setSelectedRoster(selectedRosterData);
    setStudents([]); // Clear students until they are loaded
  };

  const handleLoad = async () => {
    setError('');
    
    // Validate all required fields
    if (!selectedRoster) {
      setError('Please select a roster');
      return;
    }
    if (!tripType) {
      setError('Please select a trip type');
      return;
    }
    if (!selectedBus) {
      setError('Please select a bus');
      return;
    }
    if (!startLocation) {
      setError('Please set a start location');
      return;
    }
    if (!endLocation) {
      setError('Please set an end location');
      return;
    }

    try {
      // Load students
      const studentIds = selectedRoster.students || [];
      
      if (studentIds.length === 0) {
        setStudents([]);
        return;
      }

      // Create a query for each student ID since 'in' operator has a limit
      const studentPromises = studentIds.map(async (studentId) => {
        const studentDoc = await getDoc(doc(db, 'students', studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          
          // Fetch parent data to get location
          let location = null;
          if (studentData.parentId) {
            const parentQuery = query(
              collection(db, 'parents'),
              where('parentId', '==', studentData.parentId)
            );
            const parentSnapshot = await getDocs(parentQuery);
            if (!parentSnapshot.empty) {
              const parentData = parentSnapshot.docs[0].data();
              location = parentData.location || null;
            }
          }

          return {
            id: studentDoc.id,
            ...studentData,
            location
          };
        }
        return null;
      });

      const studentsData = (await Promise.all(studentPromises)).filter(Boolean);
      
      // Check bus capacity
      /* Commented out bus capacity check - will be used later for optimization
      const selectedBusData = buses.find(bus => bus.id === selectedBus);
      if (selectedBusData && studentsData.length > selectedBusData.seatCount) {
        setError(`Bus capacity (${selectedBusData.seatCount}) is less than roster size (${studentsData.length})`);
        setStudents([]);
        return;
      }
      */

      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
      setError('Failed to load students');
      setStudents([]);
    }
  };

  const handleStudentSelect = useCallback((studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  }, []);

  const handleSelectAllStudents = useCallback(() => {
    setSelectedStudents(prev => {
      // If all students are already selected, deselect all
      if (prev.length === students.length) {
        return [];
      }
      // Otherwise, select all students
      return students.map(student => student.id);
    });
  }, [students]);

  const handleStartLocationSelect = useCallback((location) => {
    setStartLocation(location);
    localStorage.setItem('startLocation', JSON.stringify(location));
  }, []);

  const handleEndLocationSelect = useCallback((location) => {
    setEndLocation(location);
    localStorage.setItem('endLocation', JSON.stringify(location));
  }, []);

  const handleAddStop = useCallback((location) => {
    setAdditionalStops(prev => [...prev, { ...location, id: Date.now() }]);
  }, []);

  const handleRemoveStop = useCallback((stopId) => {
    setAdditionalStops(prev => prev.filter(stop => stop.id !== stopId));
  }, []);

  // First, calculate the raw order without any dependencies on sortedStudents
  const rawOrderMap = useMemo(() => {
    const studentsWithLocations = students.filter(student => 
      selectedStudents.includes(student.id) && student.location
    );
    
    const map = {};
    
    if (globalStudentSequence && globalStudentSequence.length > 0) {
      globalStudentSequence.forEach((studentId, index) => {
        map[studentId] = index + 1;
      });
    } else if (isManualOrder) {
      // When in manual order, use the current order of students in the list
      studentsWithLocations.forEach((student, index) => {
        map[student.id] = index + 1;
      });
    } else if (optimizedWaypointOrder.length > 0) {
      // Use optimized order when not in manual mode
      optimizedWaypointOrder.forEach((waypointIdx, stopIdx) => {
        const student = studentsWithLocations[waypointIdx];
        if (student) map[student.id] = stopIdx + 1;
      });
    } else {
      // Fallback to selection order
      studentsWithLocations.forEach((student, idx) => {
        map[student.id] = idx + 1;
      });
    }
    
    return map;
  }, [students, selectedStudents, optimizedWaypointOrder, isManualOrder, globalStudentSequence]);

  // Then sort students based on the raw order
  const sortedStudents = useMemo(() => {
    if (!isSortedByRoute) return students;
    
    return [...students].sort((a, b) => {
      const aNumber = rawOrderMap[a.id] || Infinity;
      const bNumber = rawOrderMap[b.id] || Infinity;
      return aNumber - bNumber;
    });
  }, [students, isSortedByRoute, rawOrderMap]);

  const handleSortByRoute = useCallback(() => {
    setIsSortedByRoute(prev => !prev);
  }, []);

  // Update handleDragEnd to maintain sequence consistency
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    const items = Array.from(sortedStudents);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Get only the selected students with locations in their new order
    const selectedWithLocations = items
      .filter(student => selectedStudents.includes(student.id) && student.location)
      .map(student => ({
        location: new window.google.maps.LatLng(student.location.lat, student.location.lng),
        stopover: true,
        studentId: student.id
      }));

    if (selectedWithLocations.length === 0) {
      setError('No students with locations to reorder');
      return;
    }

    // Update the waypoint order to match the new sequence exactly
    const newWaypointOrder = selectedWithLocations.map((_, index) => index);

    // Update both the waypoint order and the manual order flag
    setOptimizedWaypointOrder(newWaypointOrder);
    setIsManualOrder(true);

    // Update the route with the new order
    updateRouteWithManualOrder(selectedWithLocations);
  }, [sortedStudents, selectedStudents, updateRouteWithManualOrder]);

  // Update the marker rendering to use rawOrderMap
  const renderStudentMarkers = () => {
    const studentsWithLocations = students.filter(student => 
      selectedStudents.includes(student.id) && student.location
    );

    return studentsWithLocations.map((student) => (
      <Marker
        key={student.id}
        position={student.location}
        title={`${student.firstName} ${student.lastName}`}
        icon={{
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#2563eb',
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#1e40af',
        }}
        label={{
          text: String(rawOrderMap[student.id] || ''),
          color: 'white',
          fontWeight: 'bold',
        }}
      />
    ));
  };

  // Update the student list rendering to use rawOrderMap
  const renderStudentList = () => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="students">
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="space-y-2"
          >
            {sortedStudents.map((student, index) => (
              <Draggable
                key={student.id}
                draggableId={student.id}
                index={index}
                isDragDisabled={!selectedStudents.includes(student.id) || !student.location}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`flex items-center gap-2 p-2 border rounded ${
                      snapshot.isDragging ? 'bg-blue-50 shadow-lg' : ''
                    } ${
                      !selectedStudents.includes(student.id) || !student.location
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-move'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleStudentSelect(student.id)}
                    />
                    <div className="flex-1 flex items-center gap-2">
                      <p className="font-medium">
                        {student.firstName} {student.lastName}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${student.location ? 'bg-green-500' : 'bg-red-500'}`} />
                        <p className="text-sm text-gray-500">
                          {student.location ? 'Location Set' : 'No Location Set'}
                        </p>
                      </div>
                      <div className="flex-1"></div>
                      {rawOrderMap[student.id] && (
                        <span className="inline-block w-6 h-6 rounded-full bg-blue-600 text-white text-center font-bold ml-2">
                          {rawOrderMap[student.id]}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  // Add this function to generate time options
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeString);
      }
    }
    return times;
  };

  // Helper function to get traffic model description
  const getTrafficModelDescription = (model) => {
    switch (model) {
      case 'BEST_GUESS':
        return 'Uses historical traffic data to provide balanced estimates';
      case 'PESSIMISTIC':
        return 'Conservative estimates based on worst-case traffic scenarios';
      case 'OPTIMISTIC':
        return 'Optimistic estimates assuming minimal traffic delays';
      default:
        return '';
    }
  };

  // Helper function to calculate departure time with future date handling
  const calculateDepartureTime = (timeString) => {
    if (!timeString) return new Date();
    
    const [hours, minutes] = timeString.split(':').map(Number);
    const departureTime = new Date();
    departureTime.setHours(hours, minutes, 0, 0);
    
    // If the calculated time is in the past, move it to tomorrow
    const now = new Date();
    if (departureTime <= now) {
      departureTime.setDate(departureTime.getDate() + 1);
    }
    
    return departureTime;
  };

  const handleSaveRoute = async () => {
    if (!routeName.trim()) {
      setError('Please enter a route name');
      return;
    }

    if (!selectedRoster || !startLocation || !endLocation || !selectedBus) {
      setError('Please ensure all required fields are filled before saving');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Get the selected bus details
      const selectedBusData = buses.find(bus => bus.id === selectedBus);
      
      // Get students with locations in optimized order (if any are selected)
      const studentsWithLocations = students.filter(student => 
        selectedStudents.includes(student.id) && student.location
      );
      
      // Get all selected students (including those without locations)
      const allSelectedStudents = students.filter(student => 
        selectedStudents.includes(student.id)
      );
      
      // Create the optimized student sequence
      let optimizedStudentSequence = [];
      if (globalStudentSequence.length > 0) {
        optimizedStudentSequence = globalStudentSequence
          .map((id, idx) => {
            const student = allSelectedStudents.find(s => s.id === id);
            if (!student) return null;
            return {
              id: student.id,
              firstName: student.firstName,
              lastName: student.lastName,
              location: student.location || null,
              sequence: idx + 1,
              isWaypoint: !!student.location
            };
          })
          .filter(Boolean);
      } else if (optimizedWaypointOrder.length > 0 && studentsWithLocations.length > 0) {
        // If we have waypoints, use the optimized order
        optimizedStudentSequence = optimizedWaypointOrder.map((waypointIdx, stopIdx) => {
          const student = studentsWithLocations[waypointIdx];
          return {
            id: student.id,
            firstName: student.firstName,
            lastName: student.lastName,
            location: student.location,
            sequence: stopIdx + 1,
            isWaypoint: true
          };
        });
      } else if (allSelectedStudents.length > 0) {
        // If no waypoints but students are selected, include them without locations
        optimizedStudentSequence = allSelectedStudents.map((student, idx) => ({
          id: student.id,
          firstName: student.firstName,
          lastName: student.lastName,
          location: student.location || null,
          sequence: idx + 1,
          isWaypoint: student.location ? true : false
        }));
      }

      const routeData = {
        name: routeName.trim(),
        createdAt: new Date(),
        rosterId: selectedRoster.id,
        rosterName: selectedRoster.name,
        rosterFrequency: selectedRoster.frequency || 'daily',
        tripType: tripType, // AM, PM, or FIELD_TRIP
        time: selectedTime,
        busId: selectedBus,
        busDetails: selectedBusData ? {
          fleetNumber: selectedBusData.fleetNumber,
          registrationNumber: selectedBusData.registrationNumber,
          make: selectedBusData.make,
          model: selectedBusData.model,
          seatCount: selectedBusData.seatCount
        } : null,
        startLocation: {
          lat: startLocation.lat,
          lng: startLocation.lng,
          placeName: startLocation.placeName || `${startLocation.lat}, ${startLocation.lng}`
        },
        endLocation: {
          lat: endLocation.lat,
          lng: endLocation.lng,
          placeName: endLocation.placeName || `${endLocation.lat}, ${endLocation.lng}`
        },
        additionalStops: additionalStops.map(stop => ({
          id: stop.id,
          lat: stop.lat,
          lng: stop.lng,
          placeName: stop.placeName || `${stop.lat}, ${stop.lng}`
        })),
        studentSequence: optimizedStudentSequence,
        routeMetrics: {
          duration: routeDuration || 'Direct route',
          distance: routeDistance || 'N/A',
          totalDuration: totalDuration || 0,
          totalWaitTime: totalWaitTime || 0,
          totalDurationWithWait: totalDurationWithWait || 0
        },
        trafficOptimization: {
          enabled: useTrafficOptimization,
          model: trafficModel,
          departureTime: selectedTime
        },
        optimizedWaypointOrder: optimizedWaypointOrder,
        isManualOrder: isManualOrder,
        hasWaypoints: (globalStudentSequence.length > 0) ? globalStudentSequence.length > 0 : (studentsWithLocations.length > 0),
        status: 'active'
      };

      const routeRef = collection(db, 'optimizedRoutes');
      await addDoc(routeRef, routeData);
      
      setRouteName('');
      setShowSaveModal(false);
      setError('Route saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
      
    } catch (error) {
      console.error('Error saving route:', error);
      setError('Failed to save route. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check for route data in URL parameters (for editing from OptimizedRoutes)
  const checkForRouteData = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const routeData = urlParams.get('routeData');
    
    if (routeData) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(routeData));
        loadRouteData(decodedData);
        // Clear the URL parameter after loading
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Error parsing route data from URL:', error);
        setError('Failed to load route data from URL');
      }
    }
  };

  // Load route data into the form
  const loadRouteData = async (routeData) => {
    try {
      setError('');
      
      // Set basic route information
      setTripType(routeData.tripType || 'AM');
      setSelectedTime(routeData.time || '');
      
      // Set locations
      if (routeData.startLocation) {
        setStartLocation({
          lat: routeData.startLocation.lat,
          lng: routeData.startLocation.lng,
          placeName: routeData.startLocation.placeName
        });
        localStorage.setItem('startLocation', JSON.stringify({
          lat: routeData.startLocation.lat,
          lng: routeData.startLocation.lng,
          placeName: routeData.startLocation.placeName
        }));
      }
      
      if (routeData.endLocation) {
        setEndLocation({
          lat: routeData.endLocation.lat,
          lng: routeData.endLocation.lng,
          placeName: routeData.endLocation.placeName
        });
        localStorage.setItem('endLocation', JSON.stringify({
          lat: routeData.endLocation.lat,
          lng: routeData.endLocation.lng,
          placeName: routeData.endLocation.placeName
        }));
      }
      
      // Set bus
      if (routeData.busId) {
        setSelectedBus(routeData.busId);
      }
      
      // Set additional stops
      if (routeData.additionalStops && Array.isArray(routeData.additionalStops)) {
        setAdditionalStops(routeData.additionalStops);
      } else {
        setAdditionalStops([]);
      }
      
      // Set roster and load students
      if (routeData.rosterId) {
        const roster = rosters.find(r => r.id === routeData.rosterId);
        if (roster) {
          setSelectedRoster(roster);
          await loadStudentsForRoster(roster);
          
          // Set selected students based on the saved sequence
          if (routeData.studentSequence) {
            const studentIds = routeData.studentSequence.map(student => student.id);
            setSelectedStudents(studentIds);
          }
          
          // Set optimized waypoint order if available
          if (routeData.optimizedWaypointOrder) {
            setOptimizedWaypointOrder(routeData.optimizedWaypointOrder);
            setIsManualOrder(routeData.isManualOrder || false);
          }
          
          // Set route metrics
          if (routeData.routeMetrics) {
            setRouteDuration(routeData.routeMetrics.duration);
            setRouteDistance(routeData.routeMetrics.distance);
            setTotalDuration(routeData.routeMetrics.totalDuration || 0);
            setTotalWaitTime(routeData.routeMetrics.totalWaitTime || 0);
            setTotalDurationWithWait(routeData.routeMetrics.totalDurationWithWait || 0);
          }
          
          // Set traffic optimization settings
          if (routeData.trafficOptimization) {
            setUseTrafficOptimization(routeData.trafficOptimization.enabled || false);
            setTrafficModel(routeData.trafficOptimization.model || 'BEST_GUESS');
            if (routeData.trafficOptimization.departureTime) {
              setSelectedTime(routeData.trafficOptimization.departureTime);
            }
          }
          
          setError('Route loaded successfully! You can now modify and save as a new route.');
          setTimeout(() => setError(''), 5000);
        }
      }
    } catch (error) {
      console.error('Error loading route data:', error);
      setError('Failed to load route data');
    }
  };

  // Separate function to load students for a roster
  const loadStudentsForRoster = async (roster) => {
    try {
      const studentIds = roster.students || [];
      
      if (studentIds.length === 0) {
        setStudents([]);
        return;
      }

      const studentPromises = studentIds.map(async (studentId) => {
        const studentDoc = await getDoc(doc(db, 'students', studentId));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          
          let location = null;
          if (studentData.parentId) {
            const parentQuery = query(
              collection(db, 'parents'),
              where('parentId', '==', studentData.parentId)
            );
            const parentSnapshot = await getDocs(parentQuery);
            if (!parentSnapshot.empty) {
              const parentData = parentSnapshot.docs[0].data();
              location = parentData.location || null;
            }
          }

          return {
            id: studentDoc.id,
            ...studentData,
            location
          };
        }
        return null;
      });

      const studentsData = (await Promise.all(studentPromises)).filter(Boolean);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
      setError('Failed to load students');
      setStudents([]);
    }
  };

  // Fetch saved routes for the load modal
  const fetchSavedRoutes = async () => {
    try {
      setIsLoadingRoutes(true);
      const routesRef = collection(db, 'optimizedRoutes');
      const q = query(routesRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const routesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setSavedRoutes(routesData);
    } catch (error) {
      console.error('Error fetching saved routes:', error);
      setError('Failed to load saved routes');
    } finally {
      setIsLoadingRoutes(false);
    }
  };

  // Handle loading a saved route
  const handleLoadSavedRoute = async (route) => {
    setShowLoadRouteModal(false);
    await loadRouteData(route);
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Map...</h2>
          <p className="text-gray-500 mt-2">Please wait while we initialize the map</p>
        </div>
      </div>
    );
  }


  // Enhanced traffic simulation with more realistic patterns
  const getEnhancedTrafficLevel = (day, hour, location = 'urban') => {
      // Location-based adjustments
      const locationMultiplier = {
        'urban': 1.0,      // City center - full traffic impact
        'suburban': 0.8,   // Suburbs - lighter traffic
        'highway': 1.2,    // Highways - heavier traffic
        'rural': 0.5       // Rural areas - minimal traffic
      };
      
      const multiplier = locationMultiplier[location] || 1.0;
      
      // Weekend patterns
      if (day === 0 || day === 6) { // Sunday or Saturday
        if (day === 6) { // Saturday
          if (hour >= 9 && hour <= 12) return Math.min(4, Math.round(3 * multiplier)); // Shopping hours
          if (hour >= 14 && hour <= 18) return Math.min(4, Math.round(2.5 * multiplier)); // Afternoon activities
        } else { // Sunday
          if (hour >= 10 && hour <= 14) return Math.min(4, Math.round(2 * multiplier)); // Church/brunch hours
        }
        return Math.max(1, Math.round(1.5 * multiplier)); // General weekend traffic
      }
      
      // Weekday patterns with more granular timing
      if (hour >= 6 && hour <= 10) {
        // Morning rush - peaks at 8 AM
        const rushIntensity = hour === 8 ? 4 : hour === 7 || hour === 9 ? 3.5 : 2.5;
        return Math.min(4, Math.round(rushIntensity * multiplier));
      } else if (hour >= 11 && hour <= 14) {
        // Lunch period - moderate traffic
        const lunchIntensity = hour === 12 ? 3 : 2.5;
        return Math.min(4, Math.round(lunchIntensity * multiplier));
      } else if (hour >= 16 && hour <= 20) {
        // Evening rush - peaks at 6 PM
        const rushIntensity = hour === 18 ? 4 : hour === 17 || hour === 19 ? 3.5 : 3;
        return Math.min(4, Math.round(rushIntensity * multiplier));
      } else if (hour >= 21 && hour <= 23) {
        // Evening entertainment hours
        return Math.min(4, Math.round(2 * multiplier));
      } else if (hour >= 0 && hour <= 5) {
        // Night hours
        return 1;
      } else {
        // Other daytime hours
        return Math.min(4, Math.round(2 * multiplier));
      }
    };

  const getTrafficColor = (level) => {
    switch (level) {
      case 1: return 'bg-green-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-orange-500';
      case 4: return 'bg-red-600';
      default: return 'bg-green-500';
    }
  };

  // Custom Traffic Legend Component
  const TrafficLegend = () => {
    if (!showTraffic) return null;
    
    const currentTime = new Date().toLocaleTimeString('en-US', {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const hours = ['8:00 AM', '12:00 PM', '4:00 PM', '8:00 PM'];
    const hourValues = [8, 12, 16, 20];

    return (
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-10 border border-gray-200" 
           style={{ minWidth: showWeeklyTrafficView ? '280px' : '140px' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4 text-gray-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 10V3L4 14h7v7l9-11h-7z" 
              />
            </svg>
            <span className="text-sm font-semibold text-gray-700">
              {showWeeklyTrafficView ? 'Typical traffic' : 'Traffic'}
            </span>
          </div>
          <button
            onClick={() => setShowWeeklyTrafficView(!showWeeklyTrafficView)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {showWeeklyTrafficView ? 'Live' : 'Weekly'}
          </button>
        </div>
        
        {showWeeklyTrafficView ? (
          /* Weekly Traffic Pattern View */
          <div className="space-y-2">
            {/* Time Headers */}
            <div className="flex items-center space-x-1">
              <div className="w-3"></div> {/* Space for day labels */}
              {hours.map((hour, idx) => (
                <div key={idx} className="flex-1 text-xs text-gray-500 text-center" style={{ fontSize: '10px' }}>
                  {hour.split(' ')[0]}
                </div>
              ))}
            </div>
            
            {/* Traffic Grid */}
            {days.map((day, dayIdx) => (
              <div key={dayIdx} className="flex items-center space-x-1">
                <div className="w-3 text-xs text-gray-600 font-medium text-center" style={{ fontSize: '10px' }}>
                  {day}
                </div>
                                 {hourValues.map((hour, hourIdx) => {
                   const trafficLevel = getEnhancedTrafficLevel(dayIdx, hour, 'urban');
                   return (
                    <div
                      key={hourIdx}
                      className={`flex-1 h-3 rounded-sm ${getTrafficColor(trafficLevel)}`}
                      title={`${day === 'S' ? (dayIdx === 0 ? 'Sunday' : 'Saturday') : 
                               day === 'M' ? 'Monday' : 
                               day === 'T' ? (dayIdx === 2 ? 'Tuesday' : 'Thursday') : 
                               day === 'W' ? 'Wednesday' : 'Friday'} ${hours[hourIdx]}`}
                    />
                  );
                })}
              </div>
            ))}
            
            {/* Legend */}
            <div className="pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600 font-medium">Fast</span>
                <span className="text-xs text-gray-600 font-medium">Slow</span>
              </div>
              <div className="flex h-2 rounded-sm overflow-hidden">
                <div className="flex-1 bg-green-500"></div>
                <div className="flex-1 bg-yellow-500"></div>
                <div className="flex-1 bg-orange-500"></div>
                <div className="flex-1 bg-red-600"></div>
              </div>
            </div>
          </div>
        ) : (
          /* Live Traffic View */
          <div>
            {/* Traffic Speed Legend */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600 font-medium">Fast</span>
                <span className="text-xs text-gray-600 font-medium">Slow</span>
              </div>
              <div className="flex h-2 rounded-sm overflow-hidden">
                <div className="flex-1 bg-green-500"></div>
                <div className="flex-1 bg-yellow-500"></div>
                <div className="flex-1 bg-orange-500"></div>
                <div className="flex-1 bg-red-600"></div>
              </div>
            </div>

            {/* Current Time Display */}
            <div className="pt-2 border-t border-gray-200">
              <div className="text-xs text-gray-500 text-center font-medium">
                {currentTime}
              </div>
              <div className="text-xs text-gray-400 text-center mt-1">
                Live conditions
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper: DirectionsService.route with timeout to prevent hanging spinners
  const routeWithTimeout = (directionsService, request, timeoutMs = DIRECTIONS_TIMEOUT_MS) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), timeoutMs);
      directionsService.route(request, (res, status) => {
        clearTimeout(timer);
        if (status === window.google.maps.DirectionsStatus.OK) {
          resolve(res);
        } else {
          reject(new Error(status));
        }
      });
    });
  };

  // Compute directions in segments to bypass Google waypoint limits
  const computeSegmentedDirections = async (originLatLng, destinationLatLng, allWaypoints, optimize, calculatedDepartureTime) => {
    const segments = [];
    let remaining = [...allWaypoints];
    let currentOrigin = new window.google.maps.LatLng(originLatLng.lat, originLatLng.lng);

    while (remaining.length > 0) {
      const isLastChunk = remaining.length <= MAX_INTERMEDIATE_WAYPOINTS;

      if (isLastChunk) {
        // Last segment goes to final destination; include up to MAX_INTERMEDIATE_WAYPOINTS as waypoints
        const segWaypoints = remaining.splice(0, MAX_INTERMEDIATE_WAYPOINTS);
        segments.push({
          origin: currentOrigin,
          destination: new window.google.maps.LatLng(destinationLatLng.lat, destinationLatLng.lng),
          waypoints: segWaypoints,
          destinationStudentId: null
        });
      } else {
        // Use the last waypoint in the chunk as the segment destination to maximize covered stops
        const chunk = remaining.splice(0, MAX_INTERMEDIATE_WAYPOINTS);
        const destWaypoint = chunk[chunk.length - 1];
        const segWaypoints = chunk.slice(0, chunk.length - 1);
        const segDestination = destWaypoint.location;
        segments.push({
          origin: currentOrigin,
          destination: segDestination,
          waypoints: segWaypoints,
          destinationStudentId: destWaypoint.studentId || null
        });
        currentOrigin = segDestination;
      }
    }

    const directionsService = new window.google.maps.DirectionsService();
    const segmentResults = [];
    let totalDurationSeconds = 0;
    let totalDistanceMeters = 0;
    const globalOrder = [];

    // Run sequentially to respect quotas and order
    for (const seg of segments) {
      const request = {
        origin: seg.origin,
        destination: seg.destination,
        waypoints: (seg.waypoints || []).map(({ location, stopover }) => ({ location, stopover })),
        optimizeWaypoints: optimize && (seg.waypoints?.length || 0) > 1,
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: useTrafficOptimization ? {
          departureTime: calculatedDepartureTime,
          trafficModel: window.google.maps.TrafficModel[trafficModel]
        } : undefined,
        avoidTolls: true
      };

      // Promisify the route call with timeout
      // eslint-disable-next-line no-await-in-loop
      const result = await routeWithTimeout(directionsService, request);

      segmentResults.push(result);
      const segDuration = result.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0);
      const segDistance = result.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0);
      totalDurationSeconds += segDuration;
      totalDistanceMeters += segDistance;

      // Build global order for this segment
      if (optimize && (seg.waypoints?.length || 0) > 1) {
        const order = result.routes[0].waypoint_order || [];
        order.forEach(idx => {
          const wp = seg.waypoints[idx];
          if (wp?.studentId) globalOrder.push(wp.studentId);
        });
      } else {
        seg.waypoints.forEach(wp => {
          if (wp?.studentId) globalOrder.push(wp.studentId);
        });
      }
      if (seg.destinationStudentId) {
        globalOrder.push(seg.destinationStudentId);
      }
    }

    return { segmentResults, totalDurationSeconds, totalDistanceMeters, globalStudentSequence: globalOrder };
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#f6f7fb] pt-8 pb-8">
      {/* Top Bar */}
      <div className="px-8 mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-black">Routes Engine</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowTraffic(!showTraffic)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
              showTraffic 
                ? 'bg-red-600 text-white hover:bg-red-700' 
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 10V3L4 14h7v7l9-11h-7z" 
              />
            </svg>
            <span>{showTraffic ? 'Hide Traffic' : 'Show Traffic'}</span>
          </button>
          <button
            onClick={() => {
              setShowLoadRouteModal(true);
              fetchSavedRoutes();
            }}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-600 text-white font-semibold hover:bg-gray-700 transition-colors"
          >
            <FaFolderOpen />
            <span>Load Saved Route</span>
          </button>
          <button
            onClick={handleLoad}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
          >
            Load Roster
          </button>
          {selectedRoster && (
            <button
              onClick={optimizeRoute}
              disabled={isOptimizing}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                isOptimizing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-[#00E0E0] hover:bg-[#00B8B8]'
              } text-white font-semibold transition-colors`}
            >
              {isOptimizing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Optimize Route</span>
              )}
            </button>
          )}
          {selectedRoster && startLocation && endLocation && selectedBus && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors"
            >
              <FaSave />
              <span>Save Route</span>
            </button>
          )}
        </div>
      </div>

      {/* Top Filter Section */}
      <div className="bg-white p-4 shadow-md mx-6">
        <div className="space-y-4">
          {/* Top Row - Roster Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Roster</label>
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
                value={selectedRoster?.id || ''}
                onChange={(e) => handleRosterSelect(e.target.value)}
              >
                <option value="">Select Roster</option>
                {rosters.map(roster => (
                  <option key={roster.id} value={roster.id}>
                    {roster.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trip Type</label>
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
                value={tripType}
                onChange={(e) => setTripType(e.target.value)}
              >
                <option value="AM">School Trip (AM)</option>
                <option value="PM">Home Trip (PM)</option>
                <option value="FIELD_TRIP">Field Trip</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              >
                <option value="">Select Time</option>
                {generateTimeOptions().map(time => (
                  <option key={time} value={time}>
                    {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Bus</label>
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] focus:border-[#00E0E0]"
                value={selectedBus || ''}
                onChange={(e) => setSelectedBus(e.target.value)}
              >
                <option value="">Select Bus</option>
                {buses.map(bus => (
                  <option key={bus.id} value={bus.id}>
                    Fleet #{bus.fleetNumber} - {bus.registrationNumber} ({bus.make} {bus.model})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bottom Row - Location Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Location</label>
              <div className="relative">
                <div className="pr-8">
                  <LocationSearch
                    onLocationSelect={handleStartLocationSelect}
                    placeholder="Enter start location"
                  />
                </div>
                {startLocation && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <FaMapMarkerAlt className="text-[#00E0E0] text-xl" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Location</label>
              <div className="relative">
                <div className="pr-8">
                  <LocationSearch
                    onLocationSelect={handleEndLocationSelect}
                    placeholder="Enter end location"
                  />
                </div>
                {endLocation && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <FaFlagCheckered className="text-[#00E0E0] text-xl" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Stops Section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Additional Stops</label>
                <p className="text-xs text-gray-500 mt-1">Add stops before the end location (optional)</p>
              </div>
            <div className="space-y-2">
              {additionalStops.map((stop, index) => (
                <div key={stop.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">
                      Stop {index + 1}: {stop.placeName || `${stop.lat.toFixed(4)}, ${stop.lng.toFixed(4)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveStop(stop.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="relative">
                <LocationSearch
                  onLocationSelect={(location) => {
                    handleAddStop(location);
                    // Clear the input after adding
                    const input = document.querySelector('input[placeholder="Enter stop location"]');
                    if (input) input.value = '';
                  }}
                  placeholder="Enter stop location"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Traffic Optimization Card */}
      <div className="mx-6 mt-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Traffic Optimization</h3>
              <p className="text-sm text-gray-500">Configure route optimization based on traffic conditions</p>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useTrafficOptimization"
                checked={useTrafficOptimization}
                onChange={(e) => setUseTrafficOptimization(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="useTrafficOptimization" className="text-sm font-medium text-gray-700">
                Use Traffic Optimization
              </label>
              <div className="relative group">
                <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {useTrafficOptimization 
                    ? `Uses Google's historical traffic data with ${trafficModel.toLowerCase()} model for more accurate route planning`
                    : 'Disable to ignore traffic conditions and get fastest route regardless of traffic'
                  }
                </div>
              </div>
            </div>
          </div>
          
          {useTrafficOptimization && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Traffic Model</label>
                  <select
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                    value={trafficModel}
                    onChange={(e) => setTrafficModel(e.target.value)}
                  >
                    <option value="BEST_GUESS">Best Guess (Recommended)</option>
                    <option value="PESSIMISTIC">Pessimistic (Worst Case)</option>
                    <option value="OPTIMISTIC">Optimistic (Best Case)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    {getTrafficModelDescription(trafficModel)}
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <div className="text-center">
                    <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                      trafficModel === 'BEST_GUESS' 
                        ? 'bg-blue-100 text-blue-800' 
                        : trafficModel === 'PESSIMISTIC'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {trafficModel === 'BEST_GUESS' ? '⚡ Balanced' : 
                       trafficModel === 'PESSIMISTIC' ? '⚠️ Conservative' : '🎯 Optimistic'}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {trafficModel === 'BEST_GUESS' ? 'Balanced estimates' : 
                       trafficModel === 'PESSIMISTIC' ? 'Conservative estimates' : 'Optimistic estimates'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Card */}
      {selectedRoster && (
        <div className="mx-6 mt-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Roster</p>
                <p className="font-semibold text-gray-900">{selectedRoster.name}</p>
              </div>
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Trip Type</p>
                <p className="font-semibold text-gray-900">
                  {tripType === 'AM' ? 'School Trip' : tripType === 'PM' ? 'Home Trip' : 'Field Trip'}
                </p>
              </div>
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Bus</p>
                <p className="font-semibold text-gray-900">
                  {selectedBus ? `Fleet #${buses.find(b => b.id === selectedBus)?.fleetNumber}` : 'Not selected'}
                </p>
                {selectedBus && (
                  <p className="text-xs text-gray-500 mt-1">
                    Capacity: {buses.find(b => b.id === selectedBus)?.seatCount || 'N/A'} seats
                  </p>
                )}
              </div>
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Time</p>
                <p className="font-semibold text-gray-900">
                  {selectedTime ? (
                    new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })
                  ) : (
                    selectedRoster?.date ? new Date(selectedRoster.date).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : 'Not set'
                  )}
                </p>
              </div>
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Start Location</p>
                <p className="font-semibold text-gray-900 truncate" title={
                  startLocation && startLocation.placeName
                    ? startLocation.placeName
                    : (startLocation && startLocation.lat && startLocation.lng
                        ? `${startLocation.lat}, ${startLocation.lng}`
                        : 'Not set')
                }>
                  {startLocation && startLocation.placeName
                    ? startLocation.placeName
                    : (startLocation && startLocation.lat && startLocation.lng
                        ? `${startLocation.lat}, ${startLocation.lng}`
                        : 'Not set')}
                </p>
                {startLocation && startLocation.lat && startLocation.lng && (
                  <p className="text-xs text-gray-500 mt-1">
                    {startLocation.lat}, {startLocation.lng}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">End Location</p>
                <p className="font-semibold text-gray-900 truncate" title={
                  endLocation && endLocation.placeName
                    ? endLocation.placeName
                    : (endLocation && endLocation.lat && endLocation.lng
                        ? `${endLocation.lat}, ${endLocation.lng}`
                        : 'Not set')
                }>
                  {endLocation && endLocation.placeName
                    ? endLocation.placeName
                    : (endLocation && endLocation.lat && endLocation.lng
                        ? `${endLocation.lat}, ${endLocation.lng}`
                        : 'Not set')}
                </p>
                {endLocation && endLocation.lat && endLocation.lng && (
                  <p className="text-xs text-gray-500 mt-1">
                    {endLocation.lat}, {endLocation.lng}
                  </p>
                )}
              </div>
            </div>
            {routeDuration && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Route Duration</p>
                    <p className="font-semibold text-[#00E0E0]">{routeDuration}</p>
                    <div className="mt-2 text-sm text-gray-600">
                      <p>• Driving time: {Math.floor(totalDuration / 3600)}h {Math.floor((totalDuration % 3600) / 60)}m</p>
                      <p>• Wait time: {Math.floor(totalWaitTime / 3600)}h {Math.floor((totalWaitTime % 3600) / 60)}m</p>
                      <p className="text-xs text-gray-500 mt-1">(5 min per stop)</p>
                    </div>
                  </div>
                  {(selectedTime || selectedRoster?.date) && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Estimated Arrival Time</p>
                      <p className="font-semibold text-[#00E0E0]">
                        {(() => {
                          const startTime = selectedTime || 
                            (selectedRoster?.date ? new Date(selectedRoster.date).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }) : null);
                          
                          if (!startTime) return 'Not set';

                          const [hours, minutes] = startTime.split(':').map(Number);
                          const startDate = new Date();
                          startDate.setHours(hours, minutes, 0, 0);

                          const totalMinutes = Math.floor(totalDurationWithWait / 60);
                          const arrivalDate = new Date(startDate.getTime() + totalMinutes * 60000);

                          return arrivalDate.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          });
                        })()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Distance</p>
                    <p className="font-semibold text-[#00E0E0]">{routeDistance}</p>
                    {additionalStops.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {additionalStops.length} additional stop{additionalStops.length !== 1 ? 's' : ''}
                      </p>
                    )}
                    {useTrafficOptimization && selectedTime && (
                      <div className="mt-1 flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-gray-500">Traffic optimized</span>
                      </div>
                    )}
                  </div>
                </div>
                {useTrafficOptimization && selectedTime && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Traffic Optimization</p>
                        <p className="text-sm font-medium text-gray-900">
                          {trafficModel === 'BEST_GUESS' ? 'Best Guess' : 
                           trafficModel === 'PESSIMISTIC' ? 'Pessimistic' : 'Optimistic'} Model
                        </p>
                        <p className="text-xs text-gray-500">
                          Departure: {selectedTime ? new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          }) : 'Not set'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          trafficModel === 'BEST_GUESS' 
                            ? 'bg-blue-100 text-blue-800' 
                            : trafficModel === 'PESSIMISTIC'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {trafficModel === 'BEST_GUESS' ? '⚡ Balanced' : 
                           trafficModel === 'PESSIMISTIC' ? '⚠️ Conservative' : '🎯 Optimistic'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showTraffic && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Traffic View</p>
                        <p className="text-sm font-medium text-gray-900">Real-time traffic overlay</p>
                        <p className="text-xs text-gray-500">Shows current traffic conditions on the map</p>
                      </div>
                      <div className="text-right">
                        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          🚦 Active
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!routeDuration && (selectedStudents.length > 0 || additionalStops.length > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Route Type</p>
                    <p className="font-semibold text-[#00E0E0]">Direct Route</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedStudents.length === 0 && additionalStops.length === 0 
                        ? 'No waypoints selected' 
                        : `${selectedStudents.filter(id => students.find(s => s.id === id && s.location)).length + additionalStops.length} waypoint${(selectedStudents.filter(id => students.find(s => s.id === id && s.location)).length + additionalStops.length) !== 1 ? 's' : ''} selected`}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Students on Route</p>
                    <p className="font-semibold text-[#00E0E0]">{selectedStudents.length}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {students.filter(s => selectedStudents.includes(s.id) && s.location).length} with locations
                      {additionalStops.length > 0 && (
                        <span className="ml-2">• {additionalStops.length} additional stop{additionalStops.length !== 1 ? 's' : ''}</span>
                      )}
                    </p>
                  </div>
                </div>
                {useTrafficOptimization && selectedTime && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Traffic Optimization</p>
                        <p className="text-sm font-medium text-gray-900">
                          {trafficModel === 'BEST_GUESS' ? 'Best Guess' : 
                           trafficModel === 'PESSIMISTIC' ? 'Pessimistic' : 'Optimistic'} Model
                        </p>
                        <p className="text-xs text-gray-500">
                          Departure: {selectedTime ? new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          }) : 'Not set'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          trafficModel === 'BEST_GUESS' 
                            ? 'bg-blue-100 text-blue-800' 
                            : trafficModel === 'PESSIMISTIC'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {trafficModel === 'BEST_GUESS' ? '⚡ Balanced' : 
                           trafficModel === 'PESSIMISTIC' ? '⚠️ Conservative' : '🎯 Optimistic'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add vertical space here */}
      <div style={{ height: '24px' }} />

      {/* Main Content Area */}
      <div className="flex flex-1 w-full px-6 pt-6" style={{background: '#f6f7fb'}}>
        <div className="h-full relative" style={{width: '60%', minWidth: '400px', minHeight: '400px'}} ref={mapRef}>
          {isOptimizing && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
              <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center space-y-4">
                <div className="w-12 h-12 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin" />
                <p className="text-lg font-semibold text-gray-700">Optimizing Route...</p>
                <p className="text-sm text-gray-500">Finding the most efficient path</p>
              </div>
            </div>
          )}
          <TrafficLegend />
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={12}
            onLoad={onMapLoad}
            options={{
              mapTypeControl: true,
              streetViewControl: true,
              fullscreenControl: true,
              zoomControl: true,
              gestureHandling: 'greedy',
              maxZoom: 18,
              minZoom: 3,
              disableDoubleClickZoom: false,
              scrollwheel: true,
              draggable: true,
              keyboardShortcuts: true,
              tilt: 0
            }}
          >
            {startLocation && (
              <Marker
                position={startLocation}
                title="Start Location"
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#34D399', // green
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: '#1B9A59',
                }}
              />
            )}
            {endLocation && (
              <Marker
                position={endLocation}
                title="End Location"
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#EF4444', // red
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: '#B91C1C',
                }}
              />
            )}
            {additionalStops.map((stop, index) => (
              <Marker
                key={stop.id}
                position={stop}
                title={`Stop ${index + 1}: ${stop.placeName || 'Additional Stop'}`}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: '#F59E0B', // amber
                  fillOpacity: 1,
                  strokeWeight: 2,
                  strokeColor: '#D97706',
                }}
                label={{
                  text: `S${index + 1}`,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            ))}
            {renderStudentMarkers()}
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 2,
                  }
                }}
              />
            )}
            {directionsSegments && directionsSegments.map((seg, idx) => (
              <DirectionsRenderer
                key={`segment-${idx}`}
                directions={seg}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#FF0000',
                    strokeOpacity: 1.0,
                    strokeWeight: 2,
                  }
                }}
              />
            ))}
            {showTraffic && <TrafficLayer />}
          </GoogleMap>
        </div>

        {/* Student Selection Section */}
        <div className="h-full bg-white p-4 overflow-y-auto" style={{width: '40%', minWidth: 0}}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold">Students</h2>
              <p className="text-sm text-gray-500">Select students to add as waypoints</p>
              {selectedBus && selectedStudents.length > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm">
                    <span className={selectedStudents.length > (buses.find(b => b.id === selectedBus)?.seatCount || 0) ? 'text-red-500' : 'text-gray-600'}>
                      {selectedStudents.length} selected
                    </span>
                    <span className="text-gray-500"> / </span>
                    <span className="text-gray-600">
                      {buses.find(b => b.id === selectedBus)?.seatCount || 0} seats
                    </span>
                  </p>
                  {selectedStudents.length > (buses.find(b => b.id === selectedBus)?.seatCount || 0) && (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-5 w-5 text-red-500" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                      title="Bus capacity exceeded"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleSortByRoute}
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm ${
                  isSortedByRoute 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-4 w-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" 
                  />
                </svg>
                Sort by Route
              </button>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedStudents.length === students.length && students.length > 0}
                  onChange={handleSelectAllStudents}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-600">Select All</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {renderStudentList()}
          </div>
        </div>
      </div>

      {/* Roster Selection Modal */}
      {showRosterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">Select Roster</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {rosters.map(roster => (
                <button
                  key={roster.id}
                  onClick={() => handleRosterSelect(roster.id)}
                  className="w-full text-left p-4 border rounded hover:bg-gray-50 transition-colors"
                >
                  <p className="font-medium">{roster.name}</p>
                  <p className="text-sm text-gray-500">
                    {roster.date ? new Date(roster.date).toLocaleDateString() : 'No date set'}
                  </p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowRosterModal(false)}
              className="mt-4 px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Save Route Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Save Optimized Route</h2>
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setRouteName('');
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Route Name *
                </label>
                <input
                  type="text"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="Enter a descriptive name for this route"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] focus:border-transparent"
                  autoFocus
                />
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Route Summary</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Roster:</span> {selectedRoster?.name}</p>
                  <p><span className="font-medium">Trip Type:</span> {tripType === 'AM' ? 'School Trip' : tripType === 'PM' ? 'Home Trip' : 'Field Trip'}</p>
                  <p><span className="font-medium">Bus:</span> {buses.find(b => b.id === selectedBus)?.fleetNumber || 'N/A'}</p>
                  <p><span className="font-medium">Students:</span> {selectedStudents.length}</p>
                  <p><span className="font-medium">Waypoints:</span> {students.filter(s => selectedStudents.includes(s.id) && s.location).length}</p>
                  {additionalStops.length > 0 && (
                    <p><span className="font-medium">Additional Stops:</span> {additionalStops.length}</p>
                  )}
                  {routeDuration ? (
                    <>
                      <p><span className="font-medium">Duration:</span> {routeDuration}</p>
                      <p><span className="font-medium">Distance:</span> {routeDistance}</p>
                    </>
                  ) : (
                    <p><span className="font-medium">Route Type:</span> Direct route (no waypoints)</p>
                  )}
                </div>
              </div>
              
              {error && (
                <div className={`p-3 rounded-lg text-sm ${
                  error.includes('successfully') 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {error}
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setRouteName('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRoute}
                  disabled={isSaving || !routeName.trim()}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isSaving || !routeName.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#00E0E0] text-white hover:bg-[#00B8B8]'
                  }`}
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : (
                    <span>Save Route</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load Saved Route Modal */}
      {showLoadRouteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Load Saved Route</h2>
              <button
                onClick={() => setShowLoadRouteModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            {isLoadingRoutes ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mr-3"></div>
                <span>Loading saved routes...</span>
              </div>
            ) : savedRoutes.length === 0 ? (
              <div className="text-center py-8">
                <FaFolderOpen className="text-6xl text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Saved Routes Found</h3>
                <p className="text-gray-500">
                  You haven't saved any optimized routes yet. Create and save routes to see them here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedRoutes.map((route) => (
                  <div key={route.id} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{route.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            route.tripType === 'AM' 
                              ? 'bg-blue-100 text-blue-800' 
                              : route.tripType === 'PM' 
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {route.tripType === 'AM' ? 'School Trip' : route.tripType === 'PM' ? 'Home Trip' : 'Field Trip'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
                          <div>
                            <p className="text-sm text-gray-500">Roster</p>
                            <p className="font-medium">{route.rosterName}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Bus</p>
                            <p className="font-medium">
                              {route.busDetails ? `Fleet #${route.busDetails.fleetNumber}` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Duration</p>
                            <p className="font-medium">{route.routeMetrics?.duration || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Students</p>
                            <p className="font-medium">{route.studentSequence?.length || 0}</p>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-500">
                          Created: {new Date(route.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleLoadSavedRoute(route)}
                        className="ml-4 px-4 py-2 bg-[#00E0E0] text-white rounded-lg hover:bg-[#00B8B8] transition-colors"
                      >
                        Load Route
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLoadRouteModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 text-red-700 px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}

      <style>{markerStyles}</style>
    </div>
  );
};

export default RoutesEngine;